import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../store/eventsSlice'
import type { ParticipantRole } from '../store/registrationsSlice'
import { formatLocalDateTime } from '../utils/datetime'
import {
  formatFullName,
  hasGrantedMediaConsent,
  uiRoleToApiRole,
} from '../utils/participantMappers'
import { Button } from './Button'
import { ParticipantTicketButton } from './ParticipantTicketButton'
import { ConfirmationModal } from './ConfirmationModal'
import { DetailsToggleButton } from './DetailsToggleButton'
import { RejectedRegistrationsPanel } from './RejectedRegistrationsPanel'
import { ParticipantConversationModal } from './ParticipantConversationModal'
import { TablePagination } from './TablePagination'
import { SortableColumnHeader } from './Table'
import { intersectEligibleIds } from '../utils/bulkActions'
import { usePagedParticipants } from '../hooks/usePagedParticipants'
import {
  useAcceptRegistrationMutation,
  useRejectRegistrationMutation,
  useReopenRegistrationMutation,
  useBatchUpdateParticipantsMutation,
  type ParticipantSummary,
} from '@proaxiv/server-api'

// ── Icons ─────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const XIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

// ── Expanded detail row ───────────────────────────────────────────────────────

function ExpandedRow({ reg, eventId }: { reg: ParticipantSummary; eventId: string }) {
  const { t } = useTranslation()
  return (
    <tr className="bg-pax-light-purple/20">
      <td colSpan={4} className="px-6 py-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_last_name')}
            </span>
            <p className="text-pax-black">{reg.lastName || '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_first_name')}
            </span>
            <p className="text-pax-black">{reg.firstName}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_email')}
            </span>
            <p className="text-pax-black">{reg.email}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_company')}
            </span>
            <p className="text-pax-black">{reg.companyName ?? '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_media')}
            </span>
            <p className="text-pax-black">
              {hasGrantedMediaConsent(reg) ? t('reg_media_yes') : t('reg_media_no')}
            </p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_registered_via')}
            </span>
            <p className="text-pax-black">
              {reg.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
            </p>
          </div>
          {reg.rejectionMessage && (
            <div className="col-span-full">
              <span className="text-pax-gray text-xs font-semibold uppercase">
                {t('reg_field_rejection_msg')}
              </span>
              <p className="text-pax-black">{reg.rejectionMessage}</p>
            </div>
          )}
        </div>

        <div className="mt-4">
          <span className="text-pax-gray text-xs font-semibold uppercase">
            {t('acc_additional_actions')}
          </span>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <ParticipantTicketButton eventId={eventId} participantId={reg.id} />
          </div>
        </div>
      </td>
    </tr>
  )
}

// ── Confirm modal type ────────────────────────────────────────────────────────

type ConfirmKind = 'delete' | 'accept' | 'reject'

interface PendingConfirm {
  kind: ConfirmKind
  ids: string[]
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  event: AppEvent
}

/** Registered Participants management tab inside EventManagementPage. */
export function RegisteredParticipantsTab({ event }: Props) {
  const { t, i18n } = useTranslation()

  const {
    participants: registrations,
    total: registeredTotal,
    isLoading,
    isError,
    offset: registeredOffset,
    take: registeredTake,
    sort: registeredSort,
    order: registeredOrder,
    setOffset: setRegisteredOffset,
    setTake: setRegisteredTake,
    toggleSort: toggleRegisteredSort,
  } = usePagedParticipants({
    eventId: event.id,
    status: 'REGISTERED',
    defaultSort: 'registeredAt',
    defaultOrder: 'asc',
  })

  // Independent pager/query for the Rejected panel below — its own offset/take/sort never
  // affects the pending table above, even though both live on this one tab (see plan §2).
  const {
    participants: rejectedRegistrations,
    total: rejectedTotal,
    offset: rejectedOffset,
    take: rejectedTake,
    setOffset: setRejectedOffset,
    setTake: setRejectedTake,
  } = usePagedParticipants({
    eventId: event.id,
    status: 'REJECTED',
    defaultSort: 'rejectedAt',
    defaultOrder: 'desc',
  })

  const [acceptRegistration] = useAcceptRegistrationMutation()
  const [rejectRegistration] = useRejectRegistrationMutation()
  const [reopenRegistration] = useReopenRegistrationMutation()
  const [batchUpdate] = useBatchUpdateParticipantsMutation()

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)
  const [conversationFor, setConversationFor] = useState<{
    id: string
    name: string
    kind: 'pending' | 'rejected'
  } | null>(null)

  function openConversation(row: ParticipantSummary, kind: 'pending' | 'rejected') {
    setConversationFor({
      id: row.id!,
      name: formatFullName(row.firstName, row.lastName) || (row.email ?? ''),
      kind,
    })
  }

  const selectedCount = selectedKeys.size
  const selectedRegs = registrations.filter((r) => selectedKeys.has(r.id!))
  // Same intersection pattern as every other participant tab's bulk toolbar (see
  // `intersectEligibleIds`): accept/reject only show once every selected row still qualifies —
  // here that's trivially all of them, since this table is already scoped to REGISTERED rows,
  // but the check stays explicit rather than assuming the query filter never changes.
  const selectedPendingIds = intersectEligibleIds(
    selectedRegs,
    (r) => r.id!,
    (r) => r.status === 'REGISTERED'
  )

  // ── Selection ────────────────────────────────────────────────────────────────

  function handleSelectRow(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys(checked ? new Set(registrations.map((r) => r.id!)) : new Set())
  }

  function toggleExpand(id: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ── Confirm action ────────────────────────────────────────────────────────────

  async function handleConfirm(result: {
    notify: boolean
    message?: string
    role?: ParticipantRole
  }) {
    if (!pendingConfirm) return
    const { kind, ids } = pendingConfirm

    if (kind === 'delete') {
      await batchUpdate({
        eventId: event.id,
        batchBody: {
          participantIds: ids,
          action: 'DELETE',
          sendEmailNotification: false,
        },
      })
    } else if (kind === 'accept') {
      // A role is assigned as part of acceptance; it remains editable afterwards on the
      // Accepted tab. Fall back to AUDIENCE when the picker was somehow not answered.
      const apiRole = uiRoleToApiRole(result.role ?? 'Audience') as
        | 'AUDIENCE'
        | 'SPEAKER'
        | 'STAFF'
        | 'HOST'
      if (ids.length === 1) {
        await acceptRegistration({
          participantId: ids[0],
          acceptBody: { sendEmailNotification: result.notify, role: apiRole },
        })
      } else {
        await batchUpdate({
          eventId: event.id,
          batchBody: {
            participantIds: ids,
            action: 'ACCEPT',
            sendEmailNotification: result.notify,
            role: apiRole,
          },
        })
      }
    } else if (kind === 'reject') {
      if (ids.length === 1) {
        await rejectRegistration({
          participantId: ids[0],
          rejectBody: {
            sendEmailNotification: result.notify,
            rejectionMessage: result.message,
          },
        })
      } else {
        await batchUpdate({
          eventId: event.id,
          batchBody: {
            participantIds: ids,
            action: 'REJECT',
            sendEmailNotification: result.notify,
            rejectionMessage: result.message,
          },
        })
      }
    }

    setSelectedKeys(new Set())
    setPendingConfirm(null)
  }

  /** Reopens rejected participants back to REGISTERED. Silent — no email, no confirmation. */
  function handleReopen(ids: string[]) {
    ids.forEach((participantId) => void reopenRegistration({ participantId }))
  }

  // ── Table columns ─────────────────────────────────────────────────────────────

  const columns = [
    {
      // Name, not email — the organiser needs to recognize people at a glance; the email
      // address is still available (per-participant) in the expandable Details section below.
      header: t('reg_col_name'),
      sortKey: 'lastName',
      render: (row: ParticipantSummary) => (
        <div>
          <p className="text-pax-black font-medium">
            {row.lastName} {row.firstName}
          </p>
          <p className="text-pax-gray text-xs tabular-nums">
            {row.registeredAt ? formatLocalDateTime(row.registeredAt, i18n.language) : '—'}
          </p>
        </div>
      ),
    },
    {
      header: t('reg_col_status'),
      render: () => (
        <span className="bg-pax-orange-warm/15 text-pax-orange-warm rounded-full px-2 py-0.5 text-[10px] font-semibold">
          {t('reg_status_pending')}
        </span>
      ),
    },
    {
      // Header left visually blank (#274) — the icon strip is self-explanatory in context;
      // the loop below adds an aria-label so the column keeps a real accessible name.
      header: '',
      render: (row: ParticipantSummary) => (
        <div className="flex items-center gap-1.5">
          <DetailsToggleButton
            expanded={expandedKeys.has(row.id!)}
            onClick={() => toggleExpand(row.id!)}
            ariaLabel={`Expand ${row.email}`}
          />
          <Button
            type="button"
            variant="unstyled"
            aria-label={t('conversation_action')}
            title={t('conversation_action')}
            onClick={() => openConversation(row, 'pending')}
            className="text-pax-gray hover:bg-pax-purple/15 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
          >
            <ClockIcon />
          </Button>
          <Button
            type="button"
            variant="unstyled"
            aria-label={`Accept ${row.email}`}
            onClick={() => setPendingConfirm({ kind: 'accept', ids: [row.id!] })}
            className="text-pax-green hover:bg-pax-green/10 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
          >
            <CheckIcon />
            {t('reg_accept')}
          </Button>
          <Button
            type="button"
            variant="unstyled"
            aria-label={`Reject ${row.email}`}
            onClick={() => setPendingConfirm({ kind: 'reject', ids: [row.id!] })}
            className="text-pax-red hover:bg-pax-red/10 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
          >
            <XIcon />
            {t('reg_reject')}
          </Button>
          <Button
            type="button"
            variant="unstyled"
            aria-label={`Delete ${row.email}`}
            onClick={() => setPendingConfirm({ kind: 'delete', ids: [row.id!] })}
            className="text-pax-red hover:bg-pax-red/10 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
          >
            <TrashIcon />
            {t('reg_delete')}
          </Button>
        </div>
      ),
    },
  ]

  // Build flat rows with optional expand detail
  const flatRows: React.ReactElement[] = []
  for (const reg of registrations) {
    const isSelected = selectedKeys.has(reg.id!)
    const isExpanded = expandedKeys.has(reg.id!)
    const rowCls = [
      'border-pax-purple/10 border-b transition-colors last:border-0',
      isSelected ? 'bg-pax-light-blue/40' : 'hover:bg-pax-light-purple/40',
    ].join(' ')

    flatRows.push(
      <tr key={reg.id} className={rowCls}>
        <td className="w-10 px-4 py-3">
          <input
            type="checkbox"
            aria-label={t('table_select_row', { name: reg.email })}
            checked={isSelected}
            onChange={(e) => handleSelectRow(reg.id!, e.target.checked)}
            className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
          />
        </td>
        {columns.map((col, ci) => (
          <td key={ci} className="text-pax-black px-4 py-3 text-sm">
            {col.render(reg)}
          </td>
        ))}
      </tr>
    )

    if (isExpanded) {
      flatRows.push(<ExpandedRow key={`${reg.id!}-exp`} reg={reg} eventId={event.id} />)
    }
  }

  const allSelected = registrations.length > 0 && selectedKeys.size === registrations.length
  const someSelected = selectedKeys.size > 0 && !allSelected

  // ── Loading / error states ─────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
        <div className="bg-pax-purple/10 mx-auto h-4 w-32 rounded" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="border-pax-red/20 bg-pax-red/5 rounded-2xl border p-6 text-center text-sm text-red-500">
        {t('reg_load_error')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Combined pager + bulk actions (no page-level buttons on this tab). */}
      <TablePagination
        total={registeredTotal}
        offset={registeredOffset}
        take={registeredTake}
        onOffsetChange={setRegisteredOffset}
        onTakeChange={setRegisteredTake}
        toolbarTestId="registered-toolbar"
        selectionActions={
          selectedCount > 0 ? (
            <>
              <span className="text-pax-gray text-xs font-medium">
                {t('n_selected', { count: selectedCount })}
              </span>
              {selectedPendingIds.length > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<CheckIcon />}
                    onClick={() => setPendingConfirm({ kind: 'accept', ids: selectedPendingIds })}
                  >
                    {t('reg_accept')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<XIcon />}
                    onClick={() => setPendingConfirm({ kind: 'reject', ids: selectedPendingIds })}
                  >
                    {t('reg_reject')}
                  </Button>
                </>
              )}
              <Button
                variant="danger"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() =>
                  setPendingConfirm({
                    kind: 'delete',
                    ids: intersectEligibleIds(
                      selectedRegs,
                      (r) => r.id!,
                      () => true
                    ),
                  })
                }
              >
                {t('reg_delete')}
              </Button>
            </>
          ) : undefined
        }
      >
        <div data-testid="mobile-view" className="flex flex-col gap-3 md:hidden">
          {registrations.length === 0 ? (
            rejectedRegistrations.length > 0 ? (
              <div className="border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm">
                <p className="text-pax-gray px-4 py-10 text-center text-sm">
                  {t('reg_pending_empty')}
                </p>
              </div>
            ) : (
              <p className="text-pax-gray px-4 py-10 text-center text-sm">{t('reg_empty')}</p>
            )
          ) : (
            registrations.map((reg) => {
              const isSelected = selectedKeys.has(reg.id!)
              const isExpanded = expandedKeys.has(reg.id!)
              return (
                <div
                  key={reg.id}
                  className={[
                    'border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm',
                    isSelected ? 'bg-pax-light-blue/40' : '',
                  ].join(' ')}
                >
                  <div className="mb-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      aria-label={t('table_select_row', { name: reg.email })}
                      checked={isSelected}
                      onChange={(e) => handleSelectRow(reg.id!, e.target.checked)}
                      className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-pax-black font-medium">
                        {reg.lastName} {reg.firstName}
                      </p>
                      <p className="text-pax-gray text-xs tabular-nums">
                        {reg.registeredAt
                          ? formatLocalDateTime(reg.registeredAt, i18n.language)
                          : '—'}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <span className="bg-pax-orange-warm/15 text-pax-orange-warm rounded-full px-2 py-0.5 text-[10px] font-semibold">
                          {t('reg_status_pending')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-pax-purple/20 mb-3 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-xl border bg-slate-50 p-3 text-xs">
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_last_name')}
                        </span>
                        <p className="text-pax-black">{reg.lastName || '—'}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_first_name')}
                        </span>
                        <p className="text-pax-black">{reg.firstName}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_email')}
                        </span>
                        <p className="text-pax-black break-all">{reg.email}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_company')}
                        </span>
                        <p className="text-pax-black">{reg.companyName ?? '—'}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_media')}
                        </span>
                        <p className="text-pax-black">
                          {hasGrantedMediaConsent(reg) ? t('reg_media_yes') : t('reg_media_no')}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_registered_via')}
                        </span>
                        <p className="text-pax-black">
                          {reg.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
                        </p>
                      </div>
                      {reg.rejectionMessage && (
                        <div className="col-span-2">
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_rejection_msg')}
                          </span>
                          <p className="text-pax-black">{reg.rejectionMessage}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {isExpanded && (
                    <ParticipantTicketButton
                      eventId={event.id}
                      participantId={reg.id}
                      fullWidth
                      className="mb-3"
                    />
                  )}

                  <div className="flex flex-col gap-2">
                    <DetailsToggleButton
                      variant="block"
                      expanded={isExpanded}
                      onClick={() => toggleExpand(reg.id!)}
                      ariaLabel={`Expand ${reg.email}`}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      fullWidth
                      leftIcon={<ClockIcon />}
                      onClick={() => openConversation(reg, 'pending')}
                    >
                      {t('conversation_action')}
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      fullWidth
                      leftIcon={<CheckIcon />}
                      onClick={() => setPendingConfirm({ kind: 'accept', ids: [reg.id!] })}
                    >
                      {t('reg_accept')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      fullWidth
                      leftIcon={<XIcon />}
                      onClick={() => setPendingConfirm({ kind: 'reject', ids: [reg.id!] })}
                    >
                      {t('reg_reject')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      fullWidth
                      leftIcon={<TrashIcon />}
                      onClick={() => setPendingConfirm({ kind: 'delete', ids: [reg.id!] })}
                    >
                      {t('reg_delete')}
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table — same reasoning as the mobile list above: only keep the table shell
          (header row + box) when there's a Rejected panel below to visually separate from;
          when the whole tab is empty, just show plain centered text with no box. */}
        <div data-testid="desktop-view" className="hidden md:block">
          {registrations.length === 0 && rejectedRegistrations.length === 0 ? (
            <p className="text-pax-gray px-4 py-10 text-center text-sm">{t('reg_empty')}</p>
          ) : (
            <div className="border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
                      <th className="w-10 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          aria-label={t('select_all')}
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected
                          }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                        />
                      </th>
                      {columns.map((col, i) => {
                        // Actions is always the last column — its header stays visually blank
                        // (#274) but keeps an aria-label so the column still has a real name.
                        const isActionsCol = i === columns.length - 1
                        return (
                          <th
                            key={i}
                            aria-label={isActionsCol ? t('reg_col_actions') : undefined}
                            className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider"
                          >
                            {col.sortKey ? (
                              <SortableColumnHeader
                                label={col.header}
                                field={col.sortKey}
                                activeField={registeredSort}
                                order={registeredOrder}
                                onSort={toggleRegisteredSort}
                              />
                            ) : (
                              col.header
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-pax-gray px-4 py-10 text-center text-sm">
                          {t('reg_pending_empty')}
                        </td>
                      </tr>
                    ) : (
                      flatRows
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </TablePagination>

      {/* Rejected panel only once there is at least one registration (pending or rejected).
          When the tab is fully empty, show just "No registrations yet" — no empty Rejected table. */}
      {(registrations.length > 0 || rejectedRegistrations.length > 0) && (
        <RejectedRegistrationsPanel
          registrations={rejectedRegistrations}
          event={event}
          onReopen={handleReopen}
          onConversation={(row) => openConversation(row, 'rejected')}
          total={rejectedTotal}
          offset={rejectedOffset}
          take={rejectedTake}
          onOffsetChange={setRejectedOffset}
          onTakeChange={setRejectedTake}
        />
      )}

      {pendingConfirm?.kind === 'delete' && (
        <ConfirmationModal
          title={t('reg_delete_confirm', { count: pendingConfirm.ids.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={(r) => void handleConfirm(r)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      {pendingConfirm?.kind === 'accept' && (
        <ConfirmationModal
          title={t('reg_accept_confirm', { count: pendingConfirm.ids.length })}
          notify={true}
          rolePicker={true}
          onConfirm={(r) => void handleConfirm(r)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      {pendingConfirm?.kind === 'reject' && (
        <ConfirmationModal
          title={t('reg_reject_confirm', { count: pendingConfirm.ids.length })}
          notify={true}
          rejectMessage=""
          onConfirm={(r) => void handleConfirm(r)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      {conversationFor && (
        <ParticipantConversationModal
          participantId={conversationFor.id}
          participantName={conversationFor.name}
          onClose={() => setConversationFor(null)}
          actions={
            conversationFor.kind === 'rejected' ? (
              <Button
                variant="success"
                size="sm"
                onClick={() => {
                  const id = conversationFor.id
                  setConversationFor(null)
                  handleReopen([id])
                }}
              >
                {t('btn_reopen')}
              </Button>
            ) : (
              <>
                <Button
                  variant="success"
                  size="sm"
                  leftIcon={<CheckIcon />}
                  onClick={() => {
                    const id = conversationFor.id
                    setConversationFor(null)
                    setPendingConfirm({ kind: 'accept', ids: [id] })
                  }}
                >
                  {t('reg_accept')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<XIcon />}
                  onClick={() => {
                    const id = conversationFor.id
                    setConversationFor(null)
                    setPendingConfirm({ kind: 'reject', ids: [id] })
                  }}
                >
                  {t('reg_reject')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<TrashIcon />}
                  onClick={() => {
                    const id = conversationFor.id
                    setConversationFor(null)
                    setPendingConfirm({ kind: 'delete', ids: [id] })
                  }}
                >
                  {t('reg_delete')}
                </Button>
              </>
            )
          }
        />
      )}
    </div>
  )
}
